import {
  CalendarIcon,
  DocumentIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import { blobsApiClient } from '@/services/api/blobService';
import type { BlobDetail } from '@/services/types/blob-types';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

/**
 * Blob entity type (re-export from service types)
 */
export type BlobEntity = BlobDetail;

/**
 * Blob tooltip content component
 * Pure presentation component - memoized for performance
 */
export const BlobTooltipContent = memo<EntityTooltipContentProps<BlobEntity>>(
  ({ fetchState }) => {
    // Compute derived values before early returns (Hooks must be called unconditionally)
    const blob = fetchState.status === 'success' ? fetchState.data : null;
    const createdAt = blob?.created_at;

    const formattedDate = useMemo(() => {
      if (!createdAt) return null;
      return new Date(createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }, [createdAt]);

    const formattedSize = useMemo(() => {
      if (!blob?.file_size) return null;
      const bytes = blob.file_size;
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }, [blob?.file_size]);

    // Handle loading state
    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    // Handle error state
    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="file" />;
    }

    // Handle idle or empty state - no data yet
    if (fetchState.status === 'idle' || !blob) {
      return <EmptyState entityType="file" />;
    }

    // Success state - extract data
    const { name, file_extension, collection_details } = blob;

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader
          icon={DocumentIcon}
          title={name || 'File'}
          badges={
            file_extension ? (
              <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-[10px] font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">
                {file_extension.replace('.', '').toUpperCase()}
              </span>
            ) : undefined
          }
        />

        <TooltipSection withBorder>
          {collection_details && (
            <InfoRow
              icon={FolderIcon}
              label="Collection"
              value={collection_details.name}
            />
          )}
          {formattedSize && (
            <InfoRow icon={DocumentIcon} label="Size" value={formattedSize} />
          )}
          {formattedDate && (
            <InfoRow
              icon={CalendarIcon}
              label="Uploaded"
              value={formattedDate}
            />
          )}
        </TooltipSection>
      </div>
    );
  },
);

BlobTooltipContent.displayName = 'BlobTooltipContent';

/**
 * Hoverable blob link with tooltip preview
 *
 * Shows file name, collection, size, and upload date on hover.
 * Click to navigate to the matter's documents page with the blob focused.
 *
 * @example
 * ```tsx
 * <BlobLink entityId={blobId} matterId={matterId}>
 *   document.pdf
 * </BlobLink>
 * ```
 */
export const BlobLink = createEntityLink<
  BlobEntity,
  { matterId: string; blobId: string },
  { matterId: string }
>({
  tooltipContent: BlobTooltipContent,
  getDetailUrl: (id, { matterId }) => ({
    pathname: `/matters/${matterId}/documents/`,
    search: `?blob_id=${id}`,
  }),
  useQuery: blobsApiClient.endpoints.getBlobDetail.useQuery,
  getQueryArg: (id, { matterId }) => ({ matterId, blobId: id }),
  displayName: 'BlobLink',
});
